import classNames from "classnames";
import { SensorHealthView, TRIAGE_LABELS, TriageBucket } from "../sensorHealth.utility";

/** Dot colour plus the active (selected-filter) treatment for each bucket. */
const CHIP: Record<TriageBucket, { dot: string; count: string; active: string }> = {
  working: { dot: "bg-emerald-500", count: "text-emerald-700", active: "border-emerald-300 bg-emerald-50 ring-1 ring-emerald-300" },
  "needs-look": { dot: "bg-amber-500", count: "text-amber-700", active: "border-amber-300 bg-amber-50 ring-1 ring-amber-300" },
  "not-responding": { dot: "bg-red-500", count: "text-red-700", active: "border-red-300 bg-red-50 ring-1 ring-red-300" },
  unlinked: { dot: "bg-gray-400", count: "text-gray-700", active: "border-gray-300 bg-gray-50 ring-1 ring-gray-300" },
};

const ORDER: TriageBucket[] = ["working", "needs-look", "not-responding", "unlinked"];

type SensorTriageBarProps = {
  counts: SensorHealthView["counts"];
  activeFilter: TriageBucket | null;
  onFilterChange: (bucket: TriageBucket | null) => void;
};

/**
 * The triage breakdown as four filter chips. Each doubles as a stat (mono count) and a toggle that
 * scopes the board grid to that bucket; an empty bucket stays visible but inert so the tally reads
 * consistently. Sits beneath the overview verdict as its supporting detail.
 */
export const SensorTriageBar = ({ counts, activeFilter, onFilterChange }: SensorTriageBarProps) => (
  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
    {ORDER.map((bucket) => {
      const active = activeFilter === bucket;
      const empty = counts[bucket] === 0;
      const c = CHIP[bucket];
      return (
        <button
          key={bucket}
          type="button"
          disabled={empty}
          aria-pressed={active}
          onClick={() => onFilterChange(active ? null : bucket)}
          className={classNames(
            "flex items-center gap-2.5 rounded-xl border px-3 py-2.5 text-left transition",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1",
            active ? c.active : "border-gray-200 bg-white",
            !active && !empty && "hover:border-gray-300 hover:bg-gray-50",
            empty && "opacity-45",
          )}
        >
          <span className={classNames("h-2 w-2 shrink-0 rounded-full", c.dot)} aria-hidden />
          <span className={classNames("font-mono text-xl font-semibold tabular-nums", empty ? "text-gray-400" : c.count)}>
            {counts[bucket]}
          </span>
          <span className="min-w-0 truncate text-sm font-medium text-gray-600">{TRIAGE_LABELS[bucket]}</span>
        </button>
      );
    })}
  </div>
);
